import { useTheme } from "@emotion/react";
import type { HTMLProps, ReactNode } from "react";

import { Skeleton } from "../Skeleton";

import * as styles from "./Typography.styles";

export type ParagraphType = "body" | "footnote" | "caption";
export type HeaderType = "h1" | "h2" | "h3" | "h4" | "h5";
export type TypographyType = HeaderType | ParagraphType;

interface TypographyProps extends HTMLProps<HTMLElement> {
  /**
   * Determines the heading size
   *
   * @default body
   */
  type?: TypographyType;
  /**
   * Option for overriding size of heading tags
   * Only applies when using type h1, h2, h3, h4
   */
  sizeOverride?: TypographyType;
  /**
   * Option to indent the text
   *
   * @default false
   */
  isIndented?: boolean;
  /**
   * An option to utilize the secondary weight for that typography type
   *
   * @default false
   */
  isSecondaryWeight?: boolean;
  /**
   * Option to set the block to extend 100% width
   *
   * @default false
   */
  isFullWidth?: boolean;
  /**
   * Option to remove bottom margins from element
   *
   * @default false
   */
  isMarginless?: boolean;
  /**
   * Option to have lighter font color
   *
   * @default false
   */
  isSubdued?: boolean;
  /**
   * Displays the corresponding Skeleton
   *
   * @default false
   */
  isSkeleton?: boolean;
  /**
   * Typography content, basically text
   *
   * @default undefined
   */
  children?: ReactNode;
}

const Typography = ({
  type,
  sizeOverride,
  isIndented,
  isSecondaryWeight,
  isFullWidth,
  isMarginless,
  isSubdued,
  isSkeleton,
  children,
  ref: unusedRef,
  ...rest
}: TypographyProps) => {
  const theme = useTheme();

  const titleStyleBuilder = () => {
    return [
      styles.titleBase,
      styles.titleSizes[type](isSecondaryWeight, theme),
      isSubdued && styles.isSubdued,
      isIndented && styles.indented,
      isFullWidth && styles.fullwidth,
      !isMarginless && styles.margin,
      sizeOverride && styles.sizeOverride[sizeOverride](isSecondaryWeight, theme),
    ];
  };

  const paragraphStyleBuilder = () => {
    return [
      styles.paragraphBase,
      styles.paragraphSizes[type](isSecondaryWeight, theme),
      isSubdued && styles.isSubdued,
      isFullWidth && styles.fullwidth,
      !isMarginless && styles.margin,
      sizeOverride && styles.sizeOverride[sizeOverride](isSecondaryWeight, theme),
    ];
  };

  const renderType = () => {
    switch (type) {
      case "h1":
        return (
          <h1 css={titleStyleBuilder()} {...rest}>
            {children}
          </h1>
        );
      case "h2":
        return (
          <h2 css={titleStyleBuilder()} {...rest}>
            {children}
          </h2>
        );
      case "h3":
        return (
          <h3 css={titleStyleBuilder()} {...rest}>
            {children}
          </h3>
        );
      case "h4":
        return (
          <h4 css={titleStyleBuilder()} {...rest}>
            {children}
          </h4>
        );
      case "h5":
        return (
          <h5 css={titleStyleBuilder()} {...rest}>
            {children}
          </h5>
        );
      default:
        return (
          <p css={paragraphStyleBuilder()} {...rest}>
            {children}
          </p>
        );
    }
  };

  const getSize = () => {
    if (sizeOverride) {
      return sizeOverride;
    }

    return type ? type : "body";
  };

  const getElement = () => {
    if (isSkeleton) {
      return <Skeleton type="typography" typographySize={getSize()} isFullwidth={isFullWidth} />;
    }

    return renderType();
  };

  return getElement();
};

Typography.defaultProps = {
  type: "body",
  isSecondaryWeight: false,
  isSubdued: false,
};

export { Typography, type TypographyProps };
